import { Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { CommonIntegratedArchitectureFrameworkService } from "@org-common/lib/architecture/common-integrated-architecture-framework.service";
import { InitializedEvent } from "devextreme/ui/text_box";
import { defer, EMPTY, lastValueFrom } from "rxjs";
import { catchError, finalize, switchMap, tap } from "rxjs/operators";

@Component({
    selector: "adapt-team-roles-new-role-button",
    standalone: false,
    templateUrl: "./team-roles-new-role-button.component.html",
})
export class TeamRolesNewRoleButtonComponent extends BaseComponent implements OnDestroy {
    @Input({ required: true }) public team!: Team;
    @Output() public roleAdded = new EventEmitter<Role>();

    public newRole?: Role;
    public addNewRoleToTeam = true;
    public savingRole = false;
    public editingRole = false;

    public constructor(
        private commonDataService: CommonDataService,
        private commonDialogService: AdaptCommonDialogService,
        private commonIntegratedArchitectureService: CommonIntegratedArchitectureFrameworkService,
    ) {
        super();
    }

    public ngOnDestroy() {
        // make sure this role doesn't hang around if it's not added yet
        this.newRole?.entityAspect.rejectChanges();
    }

    public focusTextBoxOnInitialise(e: InitializedEvent) {
        setTimeout(() => e.component?.focus());
    }

    public async addNewRole() {
        if (this.newRole) {
            this.newRole.entityAspect.rejectChanges();
            this.newRole = undefined;
        }

        const roleTypeCode = this.team.teamRoleTypeCode ?? RoleTypeCode.Other;
        const roleType = await lastValueFrom(this.commonIntegratedArchitectureService.getRoleTypeByCode(roleTypeCode));

        this.newRole = await lastValueFrom(this.commonIntegratedArchitectureService.createRole({ roleType }));
        this.editingRole = true;
    }

    @Autobind
    public saveNewRole() {
        if (!this.newRole || this.newRole.entityAspect.hasValidationErrors) {
            return EMPTY;
        }

        return defer(async () => {
            this.savingRole = true;

            const entities: IBreezeEntity[] = [this.newRole!];
            if (this.addNewRoleToTeam) {
                const teamRoleLocation = await lastValueFrom(this.commonIntegratedArchitectureService.createRoleLocationForTeam(this.newRole!, this.team));
                entities.push(teamRoleLocation);
            }

            return entities;
        }).pipe(
            switchMap((entities) => this.commonDataService.saveEntities(entities).pipe(
                catchError(() => this.commonDialogService.handleSaveFailure(entities)),
            )),
            tap(() => {
                this.roleAdded.emit(this.newRole!);
                this.newRole = undefined;
                this.editingRole = false;
            }),
            finalize(() => this.savingRole = false),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public cancelNewRole() {
        if (!this.newRole) {
            return EMPTY;
        }

        return this.commonDataService.remove(this.newRole).pipe(
            tap(() => {
                this.newRole = undefined;
                this.editingRole = false;
                this.savingRole = false;
            }),
        );
    }
}
